/**
 * Shape panel (FXC-9009 · AC-6 + the design's selected-element Shape pane).
 *
 * Two halves, faithful to the prototype:
 *   1. A control-surface CHOICE — "Qiqi slotted" ↔ "xfoil plain-flap" chips. This maps the
 *      flaperon to the reserved `plain-flap` element kind / `FlapSpec` (`hinge_x` + deflection)
 *      so the flexfoil prior agrees. Per the handover this is STATE-ONLY this tranche (the
 *      plain-flap geometry builder is a later concern), so the choice is surfaced + persisted as a
 *      mode the caller can read, and we expose the derived `FlapSpec` for the register/prior calls.
 *   2. Sliders for the SELECTED element only (vane / flaperon / main+hinges+cove / blowing),
 *      bound to the existing `useFoilDesign` setters. Ranges match `FoilControls.tsx` exactly.
 *
 * This re-skins the existing `FoilControls` Slider into a compact per-element view (the prototype's
 * `sliderDefs`), rather than the full all-sections list, so the panel tracks `selectedId`.
 */
import { useId } from "react";

import type { FlapSpec } from "../../../api/highlift";
import { blowingOf, elementsOf, type BlowingSource, type FlaperonDerivedElement, type HighLiftAirfoil, type PlainFlapElement, type PlainSlatElement, type VaneBSplineElement } from "../geometry";
import type { FoilSetters } from "../foil/useFoilDesign";
import type { HighliftPalette } from "../foil/useHighliftTheme";

const FONT_STACK = "'TWK Everett', 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";
const MONO_STACK = "'JetBrains Mono', 'SFMono-Regular', Menlo, monospace";

/** The control-surface synthesis choice (AC-6). `qiqi` = the slotted blown-lift section; `plain-flap`
 *  = the xfoil-style hinged flap (reserved `plain-flap` element kind / `FlapSpec`). */
export type ControlSurface = "qiqi" | "plain-flap";

/** Derive the `FlapSpec` for the prior/register calls from the deploy-hinge pivot + flaperon angle. */
export function controlSurfaceFlapSpec(config: HighLiftAirfoil, cs: ControlSurface): FlapSpec {
  return {
    hinge_x: config.design.deployHinge.pivot[0],
    hinge_y_frac: config.design.deployHinge.pivot[1],
    // Plain-flap pins the flexfoil prior to a fixed deflection (the flaperon angle); slotted leaves
    // the prior to use each point's flap_deploy_deg (omit the fixed deflection).
    deflection_deg: cs === "plain-flap" ? config.operation.flaperonAngleDeg : null,
  };
}

interface SliderDef {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  decimals?: number;
  onChange: (v: number) => void;
}

function bounds(min: number, max: number, value: number): Pick<SliderDef, "min" | "max"> {
  return { min: Math.min(min, value), max: Math.max(max, value) };
}

function vaneSliders(element: VaneBSplineElement, setters: FoilSetters): SliderDef[] {
  const s = element.shape;
  const out: SliderDef[] = [
    { label: "vane TE x", ...bounds(0.55, 0.85, s.te[0]), step: 0.005, value: s.te[0], onChange: (v) => setters.setVaneElementTe(element.id, 0, v) },
    { label: "vane TE z", ...bounds(-0.05, 0.12, s.te[1]), step: 0.005, value: s.te[1], onChange: (v) => setters.setVaneElementTe(element.id, 1, v) },
  ];
  s.points.forEach((p, i) => {
    out.push({ label: `vane p${i + 1} x`, ...bounds(0.45, 0.85, p[0]), step: 0.005, value: p[0], onChange: (v) => setters.setVaneElementPoint(element.id, i, 0, v) });
    out.push({ label: `vane p${i + 1} z`, ...bounds(-0.12, 0.12, p[1]), step: 0.005, value: p[1], onChange: (v) => setters.setVaneElementPoint(element.id, i, 1, v) });
  });
  return out;
}

function flaperonSliders(element: FlaperonDerivedElement, config: HighLiftAirfoil, setters: FoilSetters): SliderDef[] {
  const s = element.shape;
  return [
    { label: "flap upper cut x", ...bounds(0.5, 0.95, s.upperCutX), step: 0.005, value: s.upperCutX, onChange: (v) => setters.setFlaperonElementField(element.id, "upperCutX", v) },
    { label: "flap lower cut x", ...bounds(0.5, 0.95, s.lowerCutX), step: 0.005, value: s.lowerCutX, onChange: (v) => setters.setFlaperonElementField(element.id, "lowerCutX", v) },
    { label: "nose tip x", ...bounds(0.4, 0.9, s.noseTip[0]), step: 0.005, value: s.noseTip[0], onChange: (v) => setters.setFlaperonElementField(element.id, "noseTipX", v) },
    { label: "nose tip z", ...bounds(-0.1, 0.15, s.noseTip[1]), step: 0.005, value: s.noseTip[1], onChange: (v) => setters.setFlaperonElementField(element.id, "noseTipZ", v) },
    { label: "flaperon angle°", min: -30, max: 60, step: 0.5, value: config.operation.flaperonAngleDeg, decimals: 1, onChange: setters.setFlaperonDeg },
  ];
}

function blowingSliders(source: BlowingSource, config: HighLiftAirfoil, setters: FoilSetters): SliderDef[] {
  return [
    { label: "disk x", ...bounds(-0.6, 0.4, source.x), step: 0.01, value: source.x, onChange: (v) => setters.setBlowingSourceField(source.id, "x", v) },
    { label: "disk z", ...bounds(-0.6, 0.2, source.z), step: 0.01, value: source.z, onChange: (v) => setters.setBlowingSourceField(source.id, "z", v) },
    { label: "diameter", ...bounds(0.2, 1.5, source.diameter), step: 0.01, value: source.diameter, onChange: (v) => setters.setBlowingSourceField(source.id, "diameter", v) },
    { label: "tilt°", ...bounds(-30, 30, source.tiltDeg), step: 1, value: source.tiltDeg, decimals: 0, onChange: (v) => setters.setBlowingSourceField(source.id, "tiltDeg", v) },
    { label: "load Δp/q∞", min: -10, max: 10, step: 0.1, value: config.operation.diskLoading, decimals: 2, onChange: setters.setDiskLoading },
  ];
}

function plainFlapSliders(element: PlainFlapElement, setters: FoilSetters): SliderDef[] {
  // Cove depth = the lip offset aft of the hinge (the slot width); see `coveFromHinge`.
  const coveDepth = element.cove.upperCutX - element.hingeX;
  return [
    { label: "deflection°", min: -30, max: 60, step: 0.5, value: element.deflectionDeg, decimals: 1, onChange: (v) => setters.setElementDeflection(element.id, v) },
    { label: "cove depth", ...bounds(0, 0.12, coveDepth), step: 0.005, value: coveDepth, decimals: 3, onChange: (v) => setters.setElementCoveDepth(element.id, v) },
  ];
}

function plainSlatSliders(element: PlainSlatElement, setters: FoilSetters): SliderDef[] {
  return [
    { label: "droop°", min: -10, max: 40, step: 0.5, value: element.deflectionDeg, decimals: 1, onChange: (v) => setters.setElementDeflection(element.id, v) },
  ];
}

/** Slider definitions for the SELECTED element (port of the prototype's `sliderDefs`, real setters). */
function slidersFor(
  selectedId: string | null,
  config: HighLiftAirfoil,
  setters: FoilSetters,
): SliderDef[] {
  const selectedElement = elementsOf(config).find((el) => el.id === selectedId);
  if (selectedElement?.kind === "vane-bspline") return vaneSliders(selectedElement, setters);
  if (selectedElement?.kind === "flaperon-derived") return flaperonSliders(selectedElement, config, setters);
  if (selectedElement?.kind === "plain-flap") return plainFlapSliders(selectedElement, setters);
  if (selectedElement?.kind === "plain-slat") return plainSlatSliders(selectedElement, setters);
  const selectedBlowing = blowingOf(config).find((source) => source.id === selectedId);
  if (selectedBlowing) return blowingSliders(selectedBlowing, config, setters);

  const d = config.design;
  const op = config.operation;
  const m = config.main;
  // main (default) — the fixed main element + cove + deploy + speed.
  return [
    { label: "chord (m)", min: 0.3, max: 5, step: 0.05, value: m.chord, decimals: 2, onChange: setters.setChordM },
    { label: "blunt TE", min: 0, max: 0.02, step: 0.001, value: m.bluntThickness, decimals: 3, onChange: setters.setBluntThickness },
    { label: "deploy angle°", min: 0, max: 90, step: 0.5, value: op.deployAngleDeg, decimals: 1, onChange: setters.setDeployDeg },
    { label: "spoiler°", min: -30, max: 90, step: 1, value: op.spoilerAngleDeg, decimals: 0, onChange: setters.setSpoilerDeg },
    { label: "cove upper cut x", min: 0.5, max: 1, step: 0.005, value: d.cove.upperCutX, onChange: setters.setCoveUpperCutX },
    { label: "cove lower cut x", min: 0.5, max: 1, step: 0.005, value: d.cove.lowerCutX, onChange: setters.setCoveLowerCutX },
    { label: "cove back x", min: 0.5, max: 1, step: 0.005, value: d.cove.coveX, onChange: setters.setCoveBackX },
    { label: "deploy hinge x", min: 0.4, max: 0.95, step: 0.005, value: d.deployHinge.pivot[0], onChange: setters.setDeployHingeX },
    // Vane follower (FXC-9009): gain<1 => the vane deploys less than the flap; lag => it trails a
    // slot-gap behind along the deploy track. gain 1 / lag 0 = the legacy rigid assembly.
    { label: "vane follower gain", min: 0, max: 1, step: 0.05, value: d.vaneFollower?.gain ?? 1, decimals: 2, onChange: setters.setVaneFollowerGain },
    { label: "vane follower lag", min: 0, max: 0.1, step: 0.005, value: d.vaneFollower?.lagChords ?? 0, decimals: 3, onChange: setters.setVaneFollowerLag },
    { label: "speed (kt)", min: 15, max: 900, step: 1, value: op.freestreamKnots, decimals: 0, onChange: setters.setFreestreamKt },
  ];
}

function Slider({ def, palette }: { def: SliderDef; palette: HighliftPalette }) {
  const id = useId();
  const dp = def.decimals ?? (def.step < 0.01 ? 3 : def.step < 1 ? 2 : 0);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 0" }}>
      <label htmlFor={id} style={{ flex: "0 0 104px", fontSize: 11, color: palette.ink3, fontFamily: FONT_STACK }}>
        {def.label}
      </label>
      <input
        id={id}
        type="range"
        min={def.min}
        max={def.max}
        step={def.step}
        value={def.value}
        onChange={(e) => def.onChange(parseFloat(e.target.value))}
        style={{ flex: 1, accentColor: palette.primary, minWidth: 0 }}
      />
      <span style={{ flex: "0 0 46px", textAlign: "right", fontSize: 10, color: palette.ink, fontFamily: MONO_STACK }}>{def.value.toFixed(dp)}</span>
    </div>
  );
}

export interface ShapePanelProps {
  palette: HighliftPalette;
  config: HighLiftAirfoil;
  setters: FoilSetters;
  /** The selected element id (`main` | `vane` | `flaperon` | `prop` | added ids) — drives the sliders. */
  selectedId: string | null;
  /** The control-surface mode + its toggle (AC-6). */
  controlSurface: ControlSurface;
  onControlSurface: (cs: ControlSurface) => void;
}

export function ShapePanel({ palette, config, setters, selectedId, controlSurface, onControlSurface }: ShapePanelProps) {
  const defs = slidersFor(selectedId, config, setters);
  const chip = (on: boolean): React.CSSProperties => ({
    flex: 1,
    justifyContent: "center",
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontFamily: FONT_STACK,
    cursor: "pointer",
    border: `1px solid ${on ? palette.primary : palette.line}`,
    background: on ? palette.primarySoft : palette.surface,
    color: on ? palette.primary : palette.ink3,
  });
  return (
    <div>
      {/* Control-surface synthesis choice (AC-6). */}
      <div style={{ display: "flex", gap: 6, marginBottom: 9 }}>
        <span style={chip(controlSurface === "qiqi")} onClick={() => onControlSurface("qiqi")} title="Slotted blown-lift section (the deployed vane + flaperon)">
          Qiqi slotted
        </span>
        <span style={chip(controlSurface === "plain-flap")} onClick={() => onControlSurface("plain-flap")} title="Plain hinged flap (xfoil-style) — pins the flexfoil prior to a fixed deflection">
          xfoil plain-flap
        </span>
      </div>
      {defs.map((def) => (
        <Slider key={def.label} def={def} palette={palette} />
      ))}
    </div>
  );
}
